import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logo } from '../components/shared/Logo';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ConfirmModal } from '../components/shared/ConfirmModal';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import { Order, OrderStatus } from '../types';
import api, { formatCurrency } from '../services/api';
import './OrdersScreen.css';

export const OrdersScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<Set<OrderStatus>>(new Set());
  const [cancelingOrderId, setCancelingOrderId] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const handleFilterToggle = (status: OrderStatus) => {
    setSelectedFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  const handleSelectAll = () => {
    setSelectedFilters(new Set());
  };

  const handleClearFiltersAndCollapse = () => {
    setSelectedFilters(new Set());
    setIsFiltersExpanded(false);
  };

  const getFilterDisplayText = () => {
    if (selectedFilters.size === 0) {
      return `All (${orders.length})`;
    } else if (selectedFilters.size === 1) {
      const status = Array.from(selectedFilters)[0];
      const count = orders.filter(order => order.status === status).length;
      return `${status} (${count})`;
    } else {
      const totalCount = orders.filter(order => selectedFilters.has(order.status)).length;
      return `${selectedFilters.size} Filters (${totalCount})`;
    }
  };

  const handleCancelClick = (order: Order, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the order click handler from firing
    setOrderToCancel(order);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!orderToCancel) return;

    setCancelingOrderId(orderToCancel.id);
    try {
      await api.orders.cancelOrder(orderToCancel.id);
      
      // Update the order status in local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderToCancel.id 
            ? { ...order, status: OrderStatus.CANCELLED }
            : order
        )
      );
      
      // Close modal and reset state
      setShowCancelModal(false);
      setOrderToCancel(null);
      
    } catch (err: any) {
      console.error('Failed to cancel order:', err);
      setError('Failed to cancel order. Please try again.');
    } finally {
      setCancelingOrderId(null);
    }
  };

  const handleCancelModalClose = () => {
    if (cancelingOrderId) return; // Don't close while cancelling
    setShowCancelModal(false);
    setOrderToCancel(null);
  };

  useEffect(() => {
    const loadOrders = async () => {
      setLoading(true);
      setError('');

      try {
        // Load all orders from the API
        const ordersData = await api.orders.getOrders();
        setOrders(ordersData);
      } catch (err: any) {
        console.error('Failed to load orders:', err);
        setError('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  // Check for filter state from dashboard navigation
  useEffect(() => {
    const state = location.state as { filterToPending?: boolean };
    if (state?.filterToPending) {
      setSelectedFilters(new Set([OrderStatus.PENDING]));
    }
  }, [location.state]);

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return 'status-pending';
      case OrderStatus.PROCESSING:
        return 'status-processing';
      case OrderStatus.COMPLETED:
        return 'status-completed';
      case OrderStatus.DELIVERED:
        return 'status-delivered';
      case OrderStatus.CANCELLED:
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return 'fas fa-clock';
      case OrderStatus.PROCESSING:
        return 'fas fa-spinner';
      case OrderStatus.COMPLETED:
        return 'fas fa-check-circle';
      case OrderStatus.DELIVERED:
        return 'fas fa-truck';
      case OrderStatus.CANCELLED:
        return 'fas fa-times-circle';
      default:
        return 'fas fa-clock';
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredOrders = selectedFilters.size === 0 
    ? orders 
    : orders.filter(order => selectedFilters.has(order.status));

  if (loading) {
    return (
      <div className="orders-screen">
        <LoadingSpinner message="Loading your orders..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-screen">
        <div className="orders-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => navigate('/dashboard')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>My Orders</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>
          <div className="screen-content">
            <div className="error-container">
              <h2>Error Loading Orders</h2>
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>Retry</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-screen">
      <div className="orders-container">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>My Orders</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>

        <div className="screen-content">
          {/* Filter Section */}
          <div className="filter-section">
            <button 
              className="filter-toggle-button"
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            >
              <div className="filter-toggle-content">
                <i className="fas fa-filter"></i>
                <span>Filter Orders</span>
                <span className="filter-count">
                  {getFilterDisplayText()}
                </span>
              </div>
              <i className={`fas fa-chevron-${isFiltersExpanded ? 'up' : 'down'} filter-toggle-icon`}></i>
            </button>
            
            <div className={`order-filters ${isFiltersExpanded ? 'expanded' : 'collapsed'}`}>
              <button 
                className={`filter-button ${selectedFilters.size === 0 ? 'active' : ''}`}
                onClick={handleSelectAll}
              >
                <span>All</span>
                <span className="filter-badge">{orders.length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(OrderStatus.PENDING) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(OrderStatus.PENDING)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(OrderStatus.PENDING) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Pending</span>
                </div>
                <span className="filter-badge">{orders.filter(order => order.status === OrderStatus.PENDING).length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(OrderStatus.PROCESSING) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(OrderStatus.PROCESSING)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(OrderStatus.PROCESSING) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Processing</span>
                </div>
                <span className="filter-badge">{orders.filter(order => order.status === OrderStatus.PROCESSING).length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(OrderStatus.COMPLETED) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(OrderStatus.COMPLETED)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(OrderStatus.COMPLETED) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Completed</span>
                </div>
                <span className="filter-badge">{orders.filter(order => order.status === OrderStatus.COMPLETED).length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(OrderStatus.CANCELLED) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(OrderStatus.CANCELLED)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(OrderStatus.CANCELLED) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Cancelled</span>
                </div>
                <span className="filter-badge">{orders.filter(order => order.status === OrderStatus.CANCELLED).length}</span>
              </button>
              
              {selectedFilters.size > 0 && (
                <button 
                  className="clear-filters-button"
                  onClick={handleClearFiltersAndCollapse}
                >
                  <i className="fas fa-times"></i>
                  <span>Clear Filters</span>
                </button>
              )}
            </div>
          </div>

          {/* Orders List */}
          {filteredOrders.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-shopping-bag"></i>
              <h3>No orders found</h3>
              <p>
                {selectedFilters.size === 0 
                  ? "You haven't placed any orders yet."
                  : selectedFilters.size === 1
                  ? `No ${Array.from(selectedFilters)[0].toLowerCase()} orders found.`
                  : "No orders match the selected filters."
                }
              </p>
            </div>
          ) : (
            <div className="orders-list">
              {filteredOrders.map(order => (
                <div 
                  key={order.id} 
                  className={`order-card ${order.status === OrderStatus.PENDING ? 'clickable' : ''}`}
                  onClick={() => {
                    if (order.status === OrderStatus.PENDING) {
                      navigate('/order-payment', { state: { order } });
                    }
                  }}
                >
                  <div className="order-header">
                    <div className="order-info">
                      <h3>#{order.orderNumber}</h3>
                      <p className="store-name">{order.storeName}</p>
                    </div>
                    <div className={`order-status ${getStatusColor(order.status)}`}>
                      <i className={getStatusIcon(order.status)}></i>
                      <span>{order.status}</span>
                    </div>
                  </div>
                  
                  <div className="order-details">
                    <div className="order-amount">
                      <span className="amount-label">Total Amount</span>
                      <span className="amount-value">{formatCurrency(order.totalAmount)}</span>
                    </div>
                    <div className="order-date">
                      <span className="date-label">Order Date</span>
                      <span className="date-value">{formatDate(order.createdAt)}</span>
                    </div>
                    <div className="order-items-count">
                      <span className="items-label">Items</span>
                      <span className="items-value">{order.items?.length || 0} item{(order.items?.length || 0) > 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  {order.status === OrderStatus.PENDING && (
                    <div className="order-actions">
                      <button 
                        className="cancel-order-button"
                        onClick={(e) => handleCancelClick(order, e)}
                        disabled={cancelingOrderId === order.id}
                        title="Cancel Order"
                      >
                        {cancelingOrderId === order.id ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-times"></i>
                        )}
                        <span>{cancelingOrderId === order.id ? 'Cancelling...' : 'Cancel'}</span>
                      </button>
                      <div className="pay-now-indicator">
                        <i className="fas fa-credit-card"></i>
                        <span>Tap to pay</span>
                        <i className="fas fa-chevron-right"></i>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <BottomNavigation />
      
      {/* Cancel Order Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        title="Cancel Order"
        message={`Are you sure you want to cancel order #${orderToCancel?.orderNumber}? This action cannot be undone.`}
        confirmText="Yes, Cancel Order"
        cancelText="Keep Order"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
        isLoading={cancelingOrderId === orderToCancel?.id}
      />
    </div>
  );
};